import React, { useMemo } from 'react';
import Search from 'components/search';
import { getServerSideProps as newSearchPageServerSideProps, SearchPageServerSideProps as NewSearchPageServerSideProps } from 'components/search-es';
import PopupContextProvider from 'contexts/popup';
import OverlayContextProvider from 'contexts/overlay';
import Layout from 'components/layout';
import HeadData, { HeadDataType } from 'components/head-data';
import { FeaturesType, useFeaturesContext } from 'contexts';
import searchPageServerSideProps, { SearchPageServerSideProps } from 'components/search/get-server-side-props';
import { DynamicSearchPageServerSideProps, SEARCH_PAGE_ROUTE_NAME } from 'types/search_page_types';
import { createGetServerSideProps, HandlerType } from 'utils/next/shared-getServerSideProps';
import { SearchPageModel } from 'components/search-es/search_page_model';
import { MLML_AGENT_COOKIE_NAME } from 'constants/cookies';
import MapContextProvider from 'contexts/map';
import { createPublicKey } from 'crypto';
import dynamic from 'next/dynamic';
import jwt from 'jsonwebtoken';
import cookie from 'cookie';
import { isTenantExpCommercial, isTenantExpInService, isTenantExpUS } from '@tenants/utils/which-tenant';
import { ZOO_SEARCH_TITLE } from 'constants/seo';
import { applySeoRules } from 'utils/seo';
import { getTenantById, isZoocasaTenant, TenantId, TenantTypes } from '@tenants';
import { getTenantSeoConfig } from '@tenants/utils/configs';
import { DynamicPageServerSideProps } from 'types/dynamic_page_types';
import { NOT_FOUND } from 'constants/not-found';

import type { GetServerSidePropsContext } from 'next';
import Listing, { ClientAppListing } from 'data/listing';

export type ServerSidePropsType = DynamicPageServerSideProps<SearchPageServerSideProps | NewSearchPageServerSideProps, typeof SEARCH_PAGE_ROUTE_NAME>;

const NewSearch = dynamic(() => import('components/search-es'), { ssr: false });

/**
 * Applies tenant-specific SEO rules and customizations to head tags
 */
function applyHeadTagsCustomization(headTags: HeadDataType | undefined, tenant: TenantTypes) {
  // Initialize headTags with defaults
  let updatedHeadTags = headTags || {};

  // Apply tenant-specific customizations
  if (isZoocasaTenant(tenant.id)) {
    updatedHeadTags = {
      ...updatedHeadTags,
      title: ZOO_SEARCH_TITLE,
    };
  }

  // Apply SEO rules (e.g., nofollow) based on tenant configuration
  const seoConfig = getTenantSeoConfig(tenant);
  return applySeoRules(updatedHeadTags, SEARCH_PAGE_ROUTE_NAME, seoConfig);
}

const getServerSidePropsHandler: HandlerType<ServerSidePropsType> = async (context: GetServerSidePropsContext, features: FeaturesType, tenant: TenantTypes) => {
  const { req, res } = context;

  if (isTenantExpCommercial(tenant.id, features)) {
    return NOT_FOUND;
  }

  // Check Agent link only for exprealtyUS theme
  if (isTenantExpUS(tenant.id, features) || isTenantExpInService(tenant.id, features)) {
    const isAgentToken = req.url?.includes('agent_portal_token');
    if (isAgentToken) {
      try {
        // Get token from URL
        const token = req.url?.split('agent_portal_token=')[1]?.split('&')[0];

        const agentAdminPublicKey = process.env.NEXT_PUBLIC_AGENT_ADMIN_KEY;
        if (!agentAdminPublicKey) throw new Error('Agent admin public key not found');

        // Verify token
        if (token) {
          const publicKey = createPublicKey({
            key: agentAdminPublicKey.replace(/\\n/g, '\n'),
            format: 'pem',
          });

          const decoded = jwt.verify(token, publicKey, { algorithms: ['RS256'], maxAge: '5m' });
          // Set cookie with token and expiry of 7 days
          res.setHeader('Set-Cookie', cookie.serialize(MLML_AGENT_COOKIE_NAME, JSON.stringify(decoded), {
            path: '/',
            maxAge: 604800,
          }));
        }
      } catch (error) {
        console.error('### Token verification failed:', (error as any).message);
      }
    }
  }

  let result: SearchPageServerSideProps | NewSearchPageServerSideProps;
  if (features.useNewMapPins) {
    result = await newSearchPageServerSideProps();
  } else {
    result = await searchPageServerSideProps(context);
  }

  const headTags = applyHeadTagsCustomization({}, tenant);

  const props: ServerSidePropsType = {
    routeName: SEARCH_PAGE_ROUTE_NAME,
    props: {
      features,
      tenantId: tenant.id,
      headTags,
      ...result,
    },
  };

  return { props };
};

export const getServerSideProps = createGetServerSideProps<ServerSidePropsType>(getServerSidePropsHandler);

export default function SearchPage({ routeName, props, isAiSearch, setIsAiSearch }: ServerSidePropsType & { isAiSearch?: boolean; setIsAiSearch?: (value: boolean) => void }) {
  const tenant = getTenantById(props.tenantId);
  const theme = tenant.theme;

  // Use headTags from server side if available, else use default
  const headerData = {
    title: props.headTags?.title || theme.title,
    metaDescription: props.headTags?.metaDescription || theme.metaDescription,
    ...props.headTags,
  };

  return (
    <Layout routeName={routeName}>
      <HeadData data={headerData} />
      <PopupContextProvider>
        <OverlayContextProvider>
          <DynamicSearchPageContent routeName={routeName} props={props} tenantId={props.tenantId} isAiSearch={isAiSearch} setIsAiSearch={setIsAiSearch} />
        </OverlayContextProvider>
      </PopupContextProvider>
    </Layout>
  );
}

function DynamicSearchPageContent({ props, tenantId, isAiSearch, setIsAiSearch }: DynamicSearchPageServerSideProps<SearchPageServerSideProps | NewSearchPageServerSideProps> & { tenantId: TenantId; isAiSearch?: boolean; setIsAiSearch?: (value: boolean) => void }) {
  const { features } = useFeaturesContext();
  const searchPageModel = useMemo(() => new SearchPageModel(features.useLegacySearchFilter), [features.useLegacySearchFilter]);

  if (features.useNewMapPins) {
    return <>
      <NewSearch model={searchPageModel} tenantId={tenantId} isAiSearch={isAiSearch} setIsAiSearch={setIsAiSearch} />
    </>;
  } else {
    const { clusters, listingsData, meta } = props as SearchPageServerSideProps;
    const listings = listingsData.map(i => Listing.fromClientAppListing(i as ClientAppListing));
    return <>
      <MapContextProvider>
        <Search initialData={{ clusters, listings, meta }} />
      </MapContextProvider>
    </>;
  }
}